import { InvalidPaginationTypeError } from '@takeshape/errors';
import { findSchemaAtPath, getReturnShape, hasArg } from '@takeshape/schema';
import { JSONPath as jsonPath } from 'jsonpath-plus';
import set from 'lodash/set.js';
import size from 'lodash/size.js';
/**
 * Guesses a lodash-style path that equates to a json path.
 * Does not support all jsonpath features and so only works for simple paths.
 */
export function jsonPathToArray(path) {
    const array = jsonPath.toPathArray(path);
    return array.filter((pathComponent) => /^\d+$/.test(pathComponent) || /^\w+$/.test(pathComponent));
}
function setPaginationArg(args, { pageSize, pageSizeArg }) {
    if (pageSizeArg && pageSize !== undefined) {
        set(args, pageSizeArg, pageSize);
    }
}
export function pageBasedArgs(config) {
    let page = 0;
    return () => {
        page += 1;
        const args = set({}, config.pageArg, page);
        setPaginationArg(args, config);
        return args;
    };
}
export function pageBasedHasMore(totalPagesPath) {
    let page = 0;
    return (data) => {
        page += 1;
        const totalPages = jsonPath({
            json: data,
            path: totalPagesPath,
            wrap: false
        });
        return page < totalPages;
    };
}
export function cursorArgs({ cursorArg, cursorPath, pageSize, pageSizeArg }) {
    return (data) => {
        const args = set({}, pageSizeArg, pageSize);
        const cursor = jsonPath({ json: data, path: cursorPath, wrap: false });
        if (cursor) {
            set(args, cursorArg, cursor);
        }
        return args;
    };
}
export function cursorHasMore(hasMorePath) {
    return (data) => {
        const hasMore = jsonPath({ json: data, path: hasMorePath, wrap: false });
        return Boolean(hasMore);
    };
}
export function offsetArgs(config) {
    let soFar = 0;
    const { itemsPath, offsetArg } = config;
    return (data) => {
        const pageSize = size(jsonPath({ json: data, path: itemsPath, wrap: false }));
        soFar += pageSize;
        const args = set({}, offsetArg, soFar);
        setPaginationArg(args, config);
        return args;
    };
}
export function offsetHasMore(config) {
    let soFar = 0;
    const { itemTotalPath, itemsPath, pageSize } = config;
    return (data) => {
        // If there is no itemTotalPath we should fetch until the API returns no more items
        if (!itemTotalPath) {
            const pageSize = size(jsonPath({ json: data, path: itemsPath, wrap: false }));
            return pageSize !== 0;
        }
        const total = jsonPath({ json: data, path: itemTotalPath, wrap: false });
        const calculatedPageSize = pageSize ?? size(jsonPath({ json: data, path: itemsPath, wrap: false }));
        soFar += calculatedPageSize;
        return soFar < total;
    };
}
export function createPageHelper(config) {
    const { pageTotalPath, itemsPath } = config;
    return {
        getArgs: pageBasedArgs(config),
        hasMore: pageBasedHasMore(pageTotalPath),
        itemsPath
    };
}
export function createOffsetHelper(config) {
    const { itemsPath } = config;
    return {
        getArgs: offsetArgs(config),
        hasMore: offsetHasMore(config),
        itemsPath
    };
}
export function createCursorHelper(config) {
    return {
        getArgs: cursorArgs({
            ...config,
            pageSize: config.pageSize ?? 10
        }),
        hasMore: cursorHasMore(config.hasMorePath),
        itemsPath: config.itemsPath
    };
}
export function createPaginationHelper(config) {
    if (config.type === 'cursor') {
        return createCursorHelper(config);
    }
    if (config.type === 'offset') {
        return createOffsetHelper(config);
    }
    if (config.type === 'page') {
        return createPageHelper(config);
    }
    throw new InvalidPaginationTypeError(['cursor', 'offset', 'page']);
}
export function createUserDefinedPaginationHelper(queryConfig) {
    if (!queryConfig.pagination) {
        return undefined;
    }
    return createPaginationHelper(queryConfig.pagination);
}
export function getProvidedPaginationArgs(config) {
    let args = [];
    if (config.type === 'cursor') {
        args = [config.cursorArg, config.pageSizeArg];
    }
    else if (config.type === 'offset') {
        args = [config.offsetArg];
    }
    else if (config.type === 'page') {
        args = [config.pageArg];
    }
    return args;
}
export function getProvidedPaginationPaths(config) {
    let paths = [];
    if (config.type === 'cursor') {
        paths = [config.itemsPath, config.cursorPath, config.hasMorePath];
    }
    else if (config.type === 'offset') {
        paths = [config.itemsPath, config.itemTotalPath].filter(Boolean);
    }
    else if (config.type === 'page') {
        paths = [config.itemsPath, config.pageTotalPath];
    }
    return paths.map(jsonPathToArray);
}
export function createPredicate(config) {
    const requiredArgs = getProvidedPaginationArgs(config);
    const requiredPaths = getProvidedPaginationPaths(config);
    return (projectSchema, propOrQuery) => requiredArgs.every((arg) => hasArg(projectSchema, propOrQuery, arg)) &&
        requiredPaths.every((path) => {
            const returnShape = getReturnShape(projectSchema, propOrQuery);
            return returnShape && Boolean(findSchemaAtPath(projectSchema, returnShape.schema, path));
        });
}
